<template>
  <div>
    <ul
      role="tablist"
      class="d-flex align-items-center view-tabs list-unstyled"
      :class="{'justify-content-center': align === 'center'}"
    >
      <li
        v-for="(tab, i) in tabs"
        :key="i"
        :class="{ 'active': tab.isActive, 'disabled': tab.isDisabled }"
        class="view-tabs__item"
        @click="selectTab(tab.hash, $event)"
        role="presentation"
        v-show="tab.isVisible"
      >
        <a
          v-html="tab.header"
          :aria-controls="tab.hash"
          :aria-selected="tab.isActive"
          class="tabs-component-tab-a"
          href="javascript:;"
          role="tab"
        />
      </li>
    </ul>
    <div class="tabs-component-panels">
      <slot />
    </div>
  </div>
</template>

<script>
import expiringStorage from '@/expiring-storage'
export default {
  props: {
    cacheLifetime: {
      default: 5,
      type: Number,
    },
    align: {
      default: 'left',
      type: String,
      validator: (v) => ['left', 'right', 'center'].includes(v)
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        useUrlFragment: true,
        defaultTabHash: null,
      }),
    },
  },
  data: () => ({
    tabs: [],
    activeTabHash: '',
    activeTabIndex: 0,
    lastActiveTabHash: '',
  }),
  computed: {
    storageKey () {
      return `vue-tabs-component.cache.${window.location.host}${window.location.pathname}`
    },
  },
  created () {
    this.tabs = this.$children
  },
  activated () {
    this.loaded()
  },
  mounted () {
    this.loaded()
  },
  methods: {
    loaded () {
      if (this.findTab(window.location.hash)) {
        this.selectTab(window.location.hash)
        return
      }
      const previousSelectedTabHash = expiringStorage.get(this.storageKey)
      if (this.findTab(previousSelectedTabHash)) {
        this.selectTab(previousSelectedTabHash)
        return
      }
      if (this.options.defaultTabHash !== null && this.findTab('#' + this.options.defaultTabHash)) {
        this.selectTab('#' + this.options.defaultTabHash)
        return
      }
      if (this.tabs.length) {
        this.selectTab(this.tabs[0].hash)
      }
    },
    findTab (hash) {
      return this.tabs.find((tab) => tab.hash === hash)
    },
    selectTab (selectedTabHash, event) {
      // See if we should store the hash in the url fragment.
      if (event && !this.options.useUrlFragment) {
        event.preventDefault()
      }
      const selectedTab = this.findTab(selectedTabHash)
      if (! selectedTab) {
        return
      }
      if (selectedTab.isDisabled) {
        event.preventDefault()
        return
      }
      if (this.lastActiveTabHash === selectedTab.hash) {
        this.$emit('clicked', {tab: selectedTab})
        return
      }
      this.changeTab(selectedTab)
    },
    setTabVisible (hash, visible) {
      const tab = this.findTab(hash)
      if (! tab) {
        return
      }
      tab.isVisible = visible
      if (tab.isActive) {
        // If tab is active, set a different one as active.
        tab.isActive = visible
        this.tabs.every((tab, index, array) => {
          if (tab.isVisible) {
            tab.isActive = true
            return false
          }
          return true
        })
      }
    },

    activateTab (name) {
      const tab = this.tabs.find((tab) => tab.name === name)
      this.changeTab(tab)
    },

    changeTab (selectedTab) {
      this.tabs.forEach((tab) => {
        tab.isActive = (tab.hash === selectedTab.hash)
      })
      this.$emit('changed', {tab: selectedTab})
      this.activeTabHash = selectedTab.hash
      this.activeTabIndex = this.getTabIndex(selectedTab.hash)
      this.lastActiveTabHash = this.activeTabHash = selectedTab.hash
      expiringStorage.set(this.storageKey, selectedTab.hash, this.cacheLifetime)
    },

    getTabIndex (hash) {
      const tab = this.findTab(hash)

      return this.tabs.indexOf(tab)
    },

    getTabHash (index) {
      const tab = this.tabs.find((tab) => this.tabs.indexOf(tab) === index)

      if (!tab) {
        return
      }

      return tab.hash
    },

    getActiveTab () {
      return this.findTab(this.activeTabHash)
    },

    getActiveTabIndex () {
      return this.getTabIndex(this.activeTabHash)
    },
  },
}
</script>

<style lang="scss">
.view-tabs {
  overflow-x: auto;
  margin: 0;
  border-bottom: 1px solid #e2e2e2;
  .button-tabs & {
    a {
      width: 153px;
      border: 1px solid #e2e2e2;
      height: 40px;
      line-height: 40px;
      text-align: center;
      font-size: 14px;
      letter-spacing: 0.2px;
      display: block;
    }
    border-bottom: 0;
    .view-tabs {
      &__item {
        margin-left: 0px;
        &.active {
          border-bottom: 0;
          margin-top: 0;
          a {

            border: 1px solid $primary;
          }
        }
      }
    }
  }
  &__item {
    padding: 0.5rem 0;
    cursor: pointer;
    margin-left: 26px;
    &:first-child {
      margin-left: 0;
    }

    a {
      font-size: 1rem;
      text-decoration: none;
      color: $text-gray;
    }

    &.active {
      border-bottom: 2px solid $primary;
      margin-top: 2px;
      a {
        color: $primary;
      }
    }

    &.disabled {
      cursor: inherit;
      opacity: 0.4;
    }
  }

  @media(max-width: 576px) {
    a {
      font-size: 1rem;
    }
  }
}
</style>
